import { useState, useEffect } from 'react';

/**
 * Resolves one candidate by FEC candidate ID.
 *
 * The elections endpoint filters by state and election type but has no
 * candidate_id parameter, so there is no direct lookup. The ID itself carries
 * what the request needs:
 *
 *     H 2 AL 06 123
 *     | |  |   `---- district, House only
 *     | |  `-------- two letter state
 *     | `----------- first filing cycle digit
 *     `------------- office: H house, S senate, P president
 *
 * So office and state are parsed out, that one list is fetched, and the row is
 * found in it. An Alabama House list is a few dozen rows. Because everything
 * comes from the URL, the page has no dependency on location.state and works
 * identically whether it was clicked, pasted, refreshed or shared.
 *
 * @param {string} candidateId
 * @returns {{ candidate: Object|null, loading: boolean, error: Error|null }}
 */

const CANDIDATES_ENDPOINT = 'https://api.govglance.org/elections/election_candidates';

// Padded well past any single state's field. The endpoint pages with
// limit/skip and there is no count, so one generous request beats looping.
const LIST_LIMIT = 400;

const OFFICE_TO_ELECTION_TYPE = {
  H: 'House',
  S: 'Senate',
  P: 'President',
};

// House and Senate IDs carry a two letter state. Presidential IDs do not -
// they are P, a cycle digit, then a sequence - so they are matched separately
// rather than being rejected for a state that was never there.
const FEC_ID_PATTERN = /^(?<office>[HS])(?<cycle>\d)(?<state>[A-Z]{2})/;
const PRESIDENTIAL_ID_PATTERN = /^P\d{2}/;

export const parseCandidateId = (candidateId) => {
  if (!candidateId) return null;

  const normalized = candidateId.trim().toUpperCase();

  if (PRESIDENTIAL_ID_PATTERN.test(normalized)) {
    return {
      office: 'P',
      state: null,
      electionType: OFFICE_TO_ELECTION_TYPE.P,
    };
  }

  const match = FEC_ID_PATTERN.exec(normalized);
  if (!match) return null;

  const { office, state } = match.groups;

  return {
    office,
    state,
    electionType: OFFICE_TO_ELECTION_TYPE[office],
  };
};

const useCandidate = (candidateId) => {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(Boolean(candidateId));
  const [error, setError] = useState(null);

  const apiKey = process.env.REACT_APP_API_KEY;

  useEffect(() => {
    if (!candidateId) {
      setCandidate(null);
      setLoading(false);
      return undefined;
    }

    const parsed = parseCandidateId(candidateId);

    if (!parsed) {
      setCandidate(null);
      setError(new Error('Not a valid candidate ID'));
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const fetchCandidate = async () => {
      try {
        // Presidential candidates are not filed under a state, so that filter
        // is left off rather than sent empty.
        const stateParam =
          parsed.office === 'P' ? '' : `state=${parsed.state}&`;

        const url =
          `${CANDIDATES_ENDPOINT}?${stateParam}` +
          `election_type=${parsed.electionType}` +
          `&order_by=load_date&limit=${LIST_LIMIT}&skip=0`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (cancelled) return;

        const rows = Array.isArray(data) ? data : [];

        // A candidate can file across several cycles and appear more than
        // once. Prefer the most recent filing so the page shows the current
        // record rather than whichever row the endpoint happened to order
        // first.
        const matches = rows
          .filter((row) => row.candidate_id === candidateId)
          .sort(
            (a, b) => new Date(b.last_file_date) - new Date(a.last_file_date)
          );

        if (matches.length === 0) {
          setCandidate(null);
          setError(new Error('Candidate not found'));
          return;
        }

        setCandidate(matches[0]);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching candidate:', err);
        setCandidate(null);
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCandidate();

    return () => {
      cancelled = true;
    };
  }, [candidateId, apiKey]);

  return { candidate, loading, error };
};

export default useCandidate;